import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { TimeRangeFilter } from '../Policy/_types';
import { getDateRangeFromFilter } from '../Policy/_constants';
import { AppContext } from 'views/App/context';

export interface PerformanceReportingQueryParams {
  startDate: string;
  endDate: string;
  assignedModule: string;
}

interface PerformanceReportingContextValue {
  orgId: string; // Parent risk pool organization ID
  childOrgId: string; // Child organization ID
  queryParams: PerformanceReportingQueryParams;
  timeRangeFilter: TimeRangeFilter;
  setTimeRangeFilter: (filter: TimeRangeFilter) => void;
  assignedModule: string;
  setAssignedModule: (module: string) => void;
}

const PerformanceReportingContext = createContext<PerformanceReportingContextValue | undefined>(undefined);

interface PerformanceReportingProviderProps {
  children: ReactNode;
  childOrgId: string; // Child organization ID from route
  initialStartDate?: string;
  initialEndDate?: string;
  initialTimeRangeFilter?: TimeRangeFilter;
  initialAssignedModule?: string;
}

export function PerformanceReportingProvider({
  children,
  childOrgId,
  initialStartDate,
  initialEndDate,
  initialTimeRangeFilter = TimeRangeFilter.PAST_12_MONTHS,
  initialAssignedModule = '',
}: PerformanceReportingProviderProps) {
  // Get parent risk pool organization ID from AppContext
  // In risk pool context, currentOrganizationId is the parent risk pool org
  const { currentOrganizationId } = useContext(AppContext);
  const orgId = currentOrganizationId || childOrgId; // Fallback to childOrgId if not available
  const [timeRangeFilter, setTimeRangeFilter] = useState<TimeRangeFilter>(initialTimeRangeFilter);
  const [assignedModule, setAssignedModule] = useState<string>(initialAssignedModule || '');
  const [customDates, setCustomDates] = useState<{ startDate?: string; endDate?: string }>({
    startDate: initialStartDate,
    endDate: initialEndDate,
  });

  // Sync URL params with state when they change
  useEffect(() => {
    setCustomDates({
      startDate: initialStartDate,
      endDate: initialEndDate,
    });
  }, [initialStartDate, initialEndDate]);

  // Sync time range filter when it changes from URL
  useEffect(() => {
    setTimeRangeFilter(initialTimeRangeFilter);
  }, [initialTimeRangeFilter]);

  // Get dates from custom dates or calculate from filter
  const { startDate, endDate } =
    customDates.startDate && customDates.endDate
      ? { startDate: customDates.startDate, endDate: customDates.endDate }
      : getDateRangeFromFilter(timeRangeFilter);

  const queryParams: PerformanceReportingQueryParams = {
    startDate,
    endDate,
    assignedModule,
  };

  return (
    <PerformanceReportingContext.Provider
      value={{
        orgId,
        childOrgId,
        queryParams,
        timeRangeFilter,
        setTimeRangeFilter,
        assignedModule,
        setAssignedModule,
      }}
    >
      {children}
    </PerformanceReportingContext.Provider>
  );
}

export function usePerformanceReportingContext(): PerformanceReportingContextValue {
  const context = useContext(PerformanceReportingContext);
  if (context === undefined) {
    throw new Error('usePerformanceReportingContext must be used within a PerformanceReportingProvider');
  }
  return context;
}
